import Link from 'next/link';
import { entityUrl } from '@/lib/entity-url';
import { STATE_META, type TrajectoryState } from '@/components/profile/TrajectoryRead';

/**
 * EntityTrajectoryStub — the PROGRAMMATIC replacement for the banned writer
 * prose ("Descout tracks X as Building"). Renders ONE short, factual chip from
 * live entity_trajectory data — "Descout tracks <Entity> ▲ Building →" — linking
 * to the entity's dossier. The trajectory read is set by rules + hysteresis
 * upstream (entity_trajectory/{tagId}); this component is purely presentational.
 *
 * Byte-neutral when absent: the caller resolves the article's SUBJECT entity and
 * its trajectory server-side (resolveArticleTrajectory) and passes null when
 * there is no subject entity or no trajectory doc — the common case today, since
 * the entity_trajectory collection is not seeded in prod yet. No feature flag:
 * the stub self-gates entirely on data presence.
 *
 * ONE component, reused across every article surface: news/guides/analysis
 * (ArticlePageContent) + breaking_news (BreakingNewsPageContent).
 *
 * Server-component-safe — no 'use client', no interactivity.
 */

export interface EntityTrajectoryStubData {
  entityName: string;
  entitySlug: string;
  state: TrajectoryState;
}

export default function EntityTrajectoryStub({
  data,
}: {
  data: EntityTrajectoryStubData | null;
}) {
  if (!data) return null;
  const meta = STATE_META[data.state];
  // Unknown/unmapped state → render nothing (never a broken chip).
  if (!meta) return null;

  return (
    <aside
      className="mt-8 flex flex-wrap items-baseline gap-x-2 gap-y-1 rounded-lg border-[0.5px] border-divider bg-surface2 px-4 py-3"
      data-testid="entity-trajectory-stub"
    >
      <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-parchment-dim">
        Descout tracks
      </span>
      <Link
        href={entityUrl(data.entitySlug)}
        className="font-mono text-[11px] font-semibold text-parchment hover:text-scout-gold"
      >
        {data.entityName}
      </Link>
      <span className={`font-mono text-[11px] font-semibold ${meta.color}`}>
        {meta.glyph} {meta.label}
      </span>
      <Link
        href={entityUrl(data.entitySlug)}
        aria-label={`View ${data.entityName} on Descout`}
        className="ml-auto font-mono text-[11px] font-semibold text-scout-gold hover:text-scout-gold/80"
      >
        →
      </Link>
    </aside>
  );
}
